import React, { useCallback, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { FiX, FiFileText, FiUploadCloud, FiDownload } from "react-icons/fi";
import { GoSync } from "react-icons/go";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { mono, eyebrow } from "../styles";

const overlayFade = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};
`;

const Box = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
  overflow: hidden;

  .box-header {
    padding: 18px 22px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 { margin: 0; font-size: 1.05em; font-weight: 600; color: ${({ theme }) => theme.text}; letter-spacing: -0.01em; }

    button {
      width: 30px;
      height: 30px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      cursor: pointer;

      &:hover:not(:disabled) { opacity: 1; }
      &:disabled { opacity: 0.25; cursor: not-allowed; }
    }
  }

  .box-body {
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .box-footer {
    padding: 14px 22px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      ${mono};
      font-size: 0.62em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    select {
      padding: 10px 12px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.85em;
      outline: none;

      &:focus { border-color: ${tokens.brandRing}; }
    }
  }

  .helper-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    ${mono};
    font-size: 0.68em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    align-self: flex-start;
    cursor: default;

    svg { font-size: 0.95em; }
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: ${tokens.brand};
    color: white;
    border: 1px solid ${tokens.brand};
    border-radius: ${tokens.radiusMd};
    font-size: 0.85em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover:not(:disabled) { background: ${tokens.brandHover}; transform: translateY(-1px); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }

    .spin { animation: ${spin} 1s linear infinite; }
  }

  .btn-secondary {
    padding: 10px 20px;
    background: transparent;
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    font-size: 0.85em;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) { background: ${({ theme }) => surface(theme, 1)}; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
`;

const Dropzone = styled.div`
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 28px 20px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px dashed ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  cursor: pointer;
  text-align: center;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${tokens.brand}; background: ${tokens.brandSoft}; }
  &.active { border-color: ${tokens.brand}; background: ${tokens.brandSoft}; box-shadow: 0 0 0 4px ${tokens.brandRing}; }
  &.has-file { border-style: solid; border-color: ${tokens.brand}; background: ${tokens.brandSoft}; }

  .drop-icon {
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 50%;
    color: ${tokens.brand};

    svg { font-size: 1.3em; }
  }

  .drop-eyebrow { ${eyebrow}; font-size: 0.55em; opacity: 0.5; }

  h4 {
    margin: 2px 0 0 0;
    font-size: 0.88em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
  }
`;

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Modal de importação de contatos via planilha (.xlsx/.csv).
 *
 * O arquivo é enviado ao backend (POST /email-marketing/contacts/import),
 * que faz o parsing real via `exceljs` (detecta colunas de nome/email pelo
 * cabeçalho) e retorna a contagem real de contatos importados/ignorados.
 */
export default function ImportContactsModal({ isOpen, onClose, lists, onImport }) {
  const [file, setFile] = useState(null);
  const [listId, setListId] = useState(lists[0]?.id || "");
  const [processing, setProcessing] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const f = acceptedFiles[0];
    if (!f) return;
    setFile(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
    },
    multiple: false,
    disabled: processing,
  });

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setListId(lists[0]?.id || "");
    setProcessing(false);
  };

  const handleClose = () => {
    if (processing) return;
    reset();
    onClose();
  };

  const handleProcess = async () => {
    if (!file) {
      toast.error("Selecione um arquivo .xlsx ou .csv");
      return;
    }
    setProcessing(true);
    try {
      const result = await onImport(listId, file);
      const listName = lists.find((l) => l.id === listId)?.name || "lista selecionada";
      const { imported = 0, skipped = 0 } = result || {};
      if (imported > 0) {
        toast.success(
          `${imported} contato${imported !== 1 ? "s" : ""} importado${imported !== 1 ? "s" : ""} para a lista "${listName}"` +
            (skipped > 0 ? ` (${skipped} ignorado${skipped !== 1 ? "s" : ""})` : "")
        );
      } else {
        toast.warning("Nenhum contato válido encontrado na planilha");
      }
      reset();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao importar planilha");
      setProcessing(false);
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <div className="box-header">
          <h3>Importar Planilha</h3>
          <button onClick={handleClose} disabled={processing}><FiX /></button>
        </div>

        <div className="box-body">
          <Dropzone
            {...getRootProps()}
            className={`${isDragActive ? "active" : ""} ${file ? "has-file" : ""}`}
          >
            <input {...getInputProps()} />
            <div className="drop-icon"><FiFileText /></div>
            {file ? (
              <>
                <span className="drop-eyebrow">ARQUIVO SELECIONADO</span>
                <h4>{file.name}</h4>
                <p>{formatBytes(file.size)} · clique para substituir</p>
              </>
            ) : (
              <>
                <span className="drop-eyebrow">UPLOAD DE PLANILHA</span>
                <h4>Arraste o arquivo .xlsx ou .csv aqui</h4>
                <p>ou clique para selecionar do computador</p>
              </>
            )}
          </Dropzone>

          <div className="field">
            <label>Lista de destino</label>
            <select value={listId} onChange={(e) => setListId(e.target.value)}>
              {lists.length === 0 && <option value="">Nenhuma lista cadastrada</option>}
              {lists.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          <span className="helper-link">
            <FiDownload /> Baixar modelo de planilha (em breve)
          </span>
        </div>

        <div className="box-footer">
          <button className="btn-secondary" onClick={handleClose} disabled={processing}>Cancelar</button>
          <button className="btn-primary" onClick={handleProcess} disabled={processing || !file}>
            {processing ? <GoSync className="spin" /> : <FiUploadCloud />}
            {processing ? "Processando..." : "Processar"}
          </button>
        </div>
      </Box>
    </Overlay>
  );
}
